/**
 * Single source of truth for "which tenant is this UI operating on".
 *
 * The deployment is **single-tenant** and the tenant is **not selectable** in
 * the UI. It is resolved, in order:
 *   1. the backend identity from `/auth/me` (`getTenantId()`), which the server
 *      canonicalizes (e.g. legacy `dev-tenant` -> `march-health-dev`);
 *   2. the build-time `VITE_HEARTBEAT_TENANT_ID` override, for local/dev tooling;
 *   3. the canonical dev default.
 *
 * Nothing writes the tenant from the UI, so there is no setter and no persisted
 * selection. Mirrors the backend's `DEFAULT_TENANT_ID` in
 * `march.auth.tenant_resolution`.
 */

import { getTenantId } from './iamAccess';

/** Canonical dev tenant — matches backend `DEFAULT_TENANT_ID`. */
export const CANONICAL_TENANT_ID = 'march-health-dev';

function configuredTenantOverride(): string {
  if (typeof import.meta === 'undefined') return '';
  return (import.meta.env.VITE_HEARTBEAT_TENANT_ID ?? '').toString().trim();
}

/**
 * The single tenant the UI is operating on. Prefers the backend-resolved
 * identity; falls back to the configured override, then the canonical default.
 */
export function resolveActiveTenantId(): string {
  return getTenantId() || configuredTenantOverride() || CANONICAL_TENANT_ID;
}
